import React from "react"
import PropTypes from "prop-types"
import { Alert, Col, Row } from "reactstrap"
import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as Yup from "yup"
import moment from "moment"

import {
  Button,
  InputField,
  ControllerHF,
  CheckboxField,
  ComboboxFormField,
  DateInputField,
} from "components"
import {
  BILLING_SUBSCRIPTION_TARIFF_ALIAS_BUSINESS,
  BILLING_SUBSCRIPTION_TARIFF_ALIAS_OPTIONS,
} from "consts"

export const UserGenerateForm = props => {
  const { t } = useTranslation()

  const tariffOptions = BILLING_SUBSCRIPTION_TARIFF_ALIAS_OPTIONS.map(
    option => ({ value: option.value, label: t(option.label) })
  )

  const { control, handleSubmit, setError, reset, watch } = useForm({
    defaultValues: {
      email: "",
      is_manual: false,
      tariff_alias: BILLING_SUBSCRIPTION_TARIFF_ALIAS_BUSINESS,
      ends_at: null,
    },
    resolver: yupResolver(
      Yup.object().shape({
        email: Yup.string()
          .email("field.error.invalid")
          .required("field.error.required"),
        is_manual: Yup.boolean(),
        tariff_alias: Yup.string()
          .nullable()
          .when("is_manual", {
            is: true,
            then: schema => schema.required("field.error.required"),
          }),
        ends_at: Yup.date()
          .nullable()
          .min(
            moment().startOf("day").toDate(),
            "field.error.date_in_past"
          ),
      })
    ),
  })

  const isManual = watch("is_manual")

  const onSubmit = values => {
    const params = {
      email: values.email,
    }

    if (values.is_manual) {
      params.subscription = {
        tariff_alias: values.tariff_alias,
        ...(values.ends_at
          ? { ends_at: moment(values.ends_at).format("YYYY-MM-DD") }
          : {}),
      }
    }

    props.onSubmit(params, { reset, setError })
  }

  return (
    <>
      {props.error && <Alert color="danger">{props.error.message}</Alert>}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col>
            <ControllerHF
              name={"email"}
              control={control}
              component={InputField}
              id={"email"}
              label={t("field.label.email")}
              placeholder={t("field.placeholder.email")}
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <ControllerHF
              name={"is_manual"}
              control={control}
              component={CheckboxField}
              id={"is_manual"}
              label={t("manual_subscription.activate")}
            />
          </Col>
        </Row>

        {isManual && (
          <Row>
            <Col md={6}>
              <ControllerHF
                name={"tariff_alias"}
                control={control}
                component={ComboboxFormField}
                id={"tariff_alias"}
                label={t("field.label.subscription_tariff")}
                placeholder={t("field.placeholder.subscription_tariff")}
                options={tariffOptions}
                isDetermineValue
                normalize={option => (option ? option.value : null)}
              />
            </Col>

            <Col md={6}>
              <ControllerHF
                name={"ends_at"}
                control={control}
                component={DateInputField}
                id={"ends_at"}
                label={t("field.label.subscription_ends_at")}
                placeholder={t("field.placeholder.subscription_ends_at")}
              />
            </Col>
          </Row>
        )}

        <div className="mt-3">
          <div className="button-items">
            <Button
              submit
              title={"add"}
              disabled={props.loading}
              loading={props.loading}
            />

            <Button
              title={"cancel"}
              color="light"
              outline
              onClick={props.onCancel}
            />
          </div>
        </div>
      </form>
    </>
  )
}

UserGenerateForm.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.object,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
}
